import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Button, Select, MenuItem, Stack, InputLabel, FormControl, FormHelperText, Checkbox, FormControlLabel, TextField, RadioGroup, Radio, Paper, Snackbar, Alert, LinearProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { getData, postData, putData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';

const payrollBatchOptions = ['FULL TIME', 'PART TIME'];

const pfSlabOptions = [
    'Restrict Employee and Employer PF to 15,000 slab',
    'Employee and Employer PF on Actual Wage',
    'Employee PF on Actual Wage and Employer to 15,000 Slab'
];

const pfRateOptions = [10, 12, 13];
const employerPfRateOptions = [10, 12, 13];

const esicSlabOptions = [
    'Restrict Contribution to Employee Gross 21,000 slab',
    'No Restriction'
];

const hraOptions = [
    'Metropolitan City -50% of the Basic',
    'Non-Metropolitan City -40% of the Basic'
];

const incomeTaxRegimeOptions = ['Old Regime', 'New Regime'];
const tdsRateOptions = [
    'Tax Slab rate for Salaried Employee',
    'Tax Percentage for Consultant'
];

const formatDisplayText = (value) => {
    if (!value) return '';

    if (value === 'FULL TIME') return 'Full Time';
    if (value === 'PART TIME') return 'Part Time';

    const words = value.toLowerCase().replace(/_/g, ' ').split(' ');
    return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const CompensationPay = ({ navigationContext, onNavigateToPayrollCompensation, onSaveAndReturn, onFormCompletionChange }) => {
    const [searchParams] = useSearchParams();
    const empIdFromUrl = searchParams.get("employeeId");
    const [formData, setFormData] = useState({
        includeInPayroll: 'Yes',
        payrollBatch: '',
        payrollExclusionReason: '',
        isPtExempt: false,
        isAdditionalTaxExempt: false,
        includePf: 'Yes',
        pfSlabType: '',
        employeePfRate: '',
        employerPfRate: '',
        pfExemptionReason: '',
        includeEsi: 'Yes',
        esicSlabType: '',
        esiExemptionReason: '',
        includeGratuity: 'Yes',
        gratuityExemptionReason: '',
        hraType: '',
        incomeTaxRegime: '',
        tdsRateType: '',
        tdsPercentage: 0,
        isSeniorCitizen: false,
        isOtherlyDisabled: false,
        isPhysicallyHandicapped: false,
        othersExemption: false,
    });

    const [initialPayrollData, setInitialPayrollData] = useState(null);
    const [completion, setCompletion] = useState(0);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    useEffect(() => {
        const fetchEmployeePayrollData = async () => {
            try {
                const response = await getData(API_ENDPOINTS.getPayrollSettingsByEmployeeId(empIdFromUrl));
                if (response.status === 'SUCCESS') {
                    setInitialPayrollData(response.result);
                } else {
                    setIsDataLoaded(true);
                }
            } catch (error) {
                if (error.response && error.response.status !== 404) {
                    setSnackbar({
                        open: true,
                        message: error.response?.message || 'An unexpected error occurred while fetching Payroll Details.',
                        severity: 'error'
                    });
                }
                setIsDataLoaded(true);
            }
        };
        fetchEmployeePayrollData();
    }, [empIdFromUrl]);

    useEffect(() => {
        if (initialPayrollData) {
            setFormData(prevData => ({
                ...prevData,
                includeInPayroll: initialPayrollData.includeInPayroll ? 'Yes' : 'No',
                payrollBatch: initialPayrollData.payrollBatch || '',
                payrollExclusionReason: initialPayrollData.includeInPayrollReason || '',
                includePf: initialPayrollData.includePF ? 'Yes' : 'No',
                pfSlabType: initialPayrollData.pfContributionRate || '',
                employeePfRate: initialPayrollData.employeePfRate ? String(parseFloat(initialPayrollData.employeePfRate)) : '',
                employerPfRate: initialPayrollData.employerPfRate ? String(parseFloat(initialPayrollData.employerPfRate)) : '',
                pfExemptionReason: initialPayrollData.includePFReason || '',
                includeEsi: initialPayrollData.includeESI ? 'Yes' : 'No',
                esicSlabType: initialPayrollData.ESIC_ContributionBasis || '',
                esiExemptionReason: initialPayrollData.includeESIReason || '',
                includeGratuity: initialPayrollData.includeGratuity ? 'Yes' : 'No',
                gratuityExemptionReason: initialPayrollData.includeGratuityReason || '',
                hraType: initialPayrollData.HRACalculationBasis || '',
                incomeTaxRegime: initialPayrollData.taxRegime || '',
                tdsRateType: initialPayrollData.tdsType || '',
                tdsPercentage: initialPayrollData.taxPercentage ? parseFloat(initialPayrollData.taxPercentage) : 0,
                isAdditionalTaxExempt: initialPayrollData.professionTaxExemption || false,
            }));
            setIsDataLoaded(true);
        }
    }, [initialPayrollData]);

    const calculateProgress = React.useCallback(() => {
        if (!isDataLoaded) {
            setCompletion(0);
            if (typeof onFormCompletionChange === 'function') {
                onFormCompletionChange(0);
            }
            return;
        }
        let requiredFields = [];
        requiredFields.push('includeInPayroll');
        if (formData.includeInPayroll === 'No') {
            requiredFields.push('payrollExclusionReason');
        } else {
            requiredFields.push('payrollBatch');
            requiredFields.push('includePf');
            requiredFields.push('includeEsi');
            requiredFields.push('includeGratuity');
            requiredFields.push('hraType');
            requiredFields.push('incomeTaxRegime');
            requiredFields.push('tdsRateType');
            if (formData.includePf === 'Yes') {
                requiredFields.push('pfSlabType');
                requiredFields.push('employeePfRate');
                requiredFields.push('employerPfRate');
            } else {
                requiredFields.push('pfExemptionReason');
            }
            if (formData.includeEsi === 'Yes') {
                requiredFields.push('esicSlabType');
            } else {
                requiredFields.push('esiExemptionReason');
            }
            if (formData.includeGratuity === 'No') {
                requiredFields.push('gratuityExemptionReason');
            }
            if (formData.tdsRateType === 'Tax Percentage for Consultant') {
                requiredFields.push('tdsPercentage');
            }
        }
        const filledCount = requiredFields.filter(field => {
            const value = formData[field];
            if (field === 'tdsPercentage') {
                const isValid = value !== null && value !== '' && Number(value) > 0;
                return isValid;
            }
            const stringValue = String(value || '');
            const isValid = value !== null && value !== undefined && stringValue.trim() !== '';
            return isValid;
        }).length;

        const totalFields = requiredFields.length;
        const percentage = totalFields > 0 ? Math.round((filledCount / totalFields) * 100) : 0;
        setCompletion(percentage);
        if (typeof onFormCompletionChange === 'function') {
            onFormCompletionChange(percentage);
        }
    }, [formData, onFormCompletionChange, isDataLoaded]);

    useEffect(() => {
        calculateProgress();
    }, [calculateProgress]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: name === 'tdsPercentage' ? Number(value) : value
        }));
    };

    const handleRadioChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: checked
        }));
    };

    const getFieldLabel = (fieldName) => {
        const fieldLabels = {
            payrollBatch: 'Payroll Batch',
            payrollExclusionReason: 'Payroll Exclusion Reason',
            pfSlabType: 'PF Contribution Rate/Slab',
            employeePfRate: 'Employee PF Rate',
            employerPfRate: 'Employer PF Rate',
            pfExemptionReason: 'PF Exemption Reason',
            esicSlabType: 'ESIC Contribution Basis',
            esiExemptionReason: 'ESI Exemption Reason',
            gratuityExemptionReason: 'Gratuity Exemption Reason',
            hraType: 'HRA Calculation Basis',
            incomeTaxRegime: 'Income Tax Regime',
            tdsRateType: 'TDS Rate Type',
            tdsPercentage: 'TDS Percentage'
        };
        return fieldLabels[fieldName] || fieldName;
    };

    const validateForm = (data) => {
        const errors = {};
        if (data.includeInPayroll === 'No') {
            if (!data.payrollExclusionReason?.trim()) {
                errors.payrollExclusionReason = 'Please provide a reason for payroll exclusion';
            }
        } else {
            if (!data.payrollBatch) {
                errors.payrollBatch = 'Please select a Payroll Batch';
            }
            if (!data.hraType?.trim()) {
                errors.hraType = 'Please select HRA Calculation Basis';
            }
            if (!data.incomeTaxRegime?.trim()) {
                errors.incomeTaxRegime = 'Please select Income Tax Regime';
            }
            if (!data.tdsRateType?.trim()) {
                errors.tdsRateType = 'Please select TDS Rate Type';
            }
            if (data.includePf === 'Yes') {
                if (!data.pfSlabType?.trim()) {
                    errors.pfSlabType = 'Please select PF Contribution Rate/Slab';
                }
                if (!data.employeePfRate || Number(data.employeePfRate) <= 0) {
                    errors.employeePfRate = 'Please specify a valid Employee PF Rate';
                }
                if (!data.employerPfRate || Number(data.employerPfRate) <= 0) {
                    errors.employerPfRate = 'Please specify a valid Employer PF Rate';
                }
            } else {
                if (!data.pfExemptionReason?.trim()) {
                    errors.pfExemptionReason = 'Please provide a reason for PF exemption';
                }
            }

            if (data.includeEsi === 'Yes') {
                if (!data.esicSlabType) {
                    errors.esicSlabType = 'Please select ESIC Contribution Basis';
                }
            } else {
                if (!data.esiExemptionReason?.trim()) {
                    errors.esiExemptionReason = 'Please provide a reason for ESI exemption';
                }
            }

            if (data.includeGratuity === 'No' && !data.gratuityExemptionReason?.trim()) {
                errors.gratuityExemptionReason = 'Please provide a reason for Gratuity exemption';
            }

            if (data.tdsRateType === 'Tax Percentage for Consultant' &&
                (!data.tdsPercentage || Number(data.tdsPercentage) <= 0)) {
                errors.tdsPercentage = 'Please enter a valid TDS Percentage for Consultant';
            }
        }
        return errors;
    };

    const hasFormDataChanged = () => {
        if (!initialPayrollData) return true;
        const getCurrentExemptionCategory = () => {
            if (formData.isSeniorCitizen) return 'senior-citizen';
            else if (formData.isOtherlyDisabled) return 'other-disabled';
            else if (formData.isPhysicallyHandicapped) return 'physically-handicapped';
            else if (formData.othersExemption) return 'others';
            return null;
        };

        const currentExemptionCategory = getCurrentExemptionCategory();
        const compareRates = (formRate, initialRate) => {
            const formVal = formRate === '' ? null : String(Math.round(parseFloat(formRate) || 0));
            const initialVal = initialRate === null || initialRate === undefined || initialRate === ''
                ? null
                : String(Math.round(parseFloat(initialRate)));
            return formVal !== initialVal;
        };

        return (
            (formData.includeInPayroll === 'Yes') !== initialPayrollData.includeInPayroll ||
            formData.payrollBatch !== initialPayrollData.payrollBatch ||
            (formData.includePf === 'Yes') !== initialPayrollData.includePF ||
            formData.pfSlabType !== initialPayrollData.pfContributionRate ||
            compareRates(formData.employeePfRate, initialPayrollData.employeePfRate) ||
            compareRates(formData.employerPfRate, initialPayrollData.employerPfRate) ||
            (formData.includePf === 'No' ? formData.pfExemptionReason : '') !== (initialPayrollData.includePFReason || '') ||
            (formData.includeEsi === 'Yes') !== initialPayrollData.includeESI ||
            formData.esicSlabType !== initialPayrollData.ESIC_ContributionBasis ||
            (formData.includeEsi === 'No' ? formData.esiExemptionReason : '') !== (initialPayrollData.includeESIReason || '') ||
            (formData.includeGratuity === 'Yes') !== initialPayrollData.includeGratuity ||
            formData.hraType !== initialPayrollData.HRACalculationBasis ||
            (formData.includeGratuity === 'No' ? formData.gratuityExemptionReason : '') !== (initialPayrollData.includeGratuityReason || '') ||
            formData.incomeTaxRegime !== initialPayrollData.taxRegime ||
            formData.tdsRateType !== initialPayrollData.tdsType ||
            Number(formData.tdsPercentage) !== parseFloat(initialPayrollData.taxPercentage) ||
            formData.isAdditionalTaxExempt !== initialPayrollData.professionTaxExemption ||
            currentExemptionCategory !== initialPayrollData.exemptionCategory ||
            (formData.includeInPayroll === 'No' ? formData.payrollExclusionReason : '') !== (initialPayrollData.includeInPayrollReason || '')
        );
    };

    const preparePayload = () => {
        let exemptionCategory = null;
        if (formData.isSeniorCitizen) exemptionCategory = 'senior-citizen';
        else if (formData.isOtherlyDisabled) exemptionCategory = 'other-disabled';
        else if (formData.isPhysicallyHandicapped) exemptionCategory = 'physically-handicapped';
        else if (formData.othersExemption) exemptionCategory = 'others';

        const convertRate = (rate) => {
            if (rate === '' || rate === null || rate === undefined) return null;
            const num = parseFloat(rate);
            return isNaN(num) ? null : num;
        };

        const payload = {
            employeePersonalId: empIdFromUrl,
            includeInPayroll: formData.includeInPayroll === 'Yes',
            includeInPayrollReason: formData.includeInPayroll === 'No' ? formData.payrollExclusionReason : null,
            payrollBatch: formData.includeInPayroll === 'Yes' ? formData.payrollBatch : null,
            includePF: formData.includeInPayroll === 'Yes' ? (formData.includePf === 'Yes') : null,
            includePFReason: formData.includeInPayroll === 'Yes' && formData.includePf === 'No' ? formData.pfExemptionReason : null,
            pfContributionRate: formData.includeInPayroll === 'Yes' && formData.includePf === 'Yes' ? formData.pfSlabType : null,
            employeePfRate: formData.includeInPayroll === 'Yes' && formData.includePf === 'Yes' ? convertRate(formData.employeePfRate) : null,
            employerPfRate: formData.includeInPayroll === 'Yes' && formData.includePf === 'Yes' ? convertRate(formData.employerPfRate) : null,
            includeESI: formData.includeInPayroll === 'Yes' ? (formData.includeEsi === 'Yes') : null,
            includeESIReason: formData.includeInPayroll === 'Yes' && formData.includeEsi === 'No' ? formData.esiExemptionReason : null,
            ESIC_ContributionBasis: formData.includeInPayroll === 'Yes' && formData.includeEsi === 'Yes' ? formData.esicSlabType : null,
            includeGratuity: formData.includeInPayroll === 'Yes' ? (formData.includeGratuity === 'Yes') : null,
            includeGratuityReason: formData.includeInPayroll === 'Yes' && formData.includeGratuity === 'No' ? formData.gratuityExemptionReason : null,
            HRACalculationBasis: formData.includeInPayroll === 'Yes' ? formData.hraType : null,
            taxRegime: formData.includeInPayroll === 'Yes' ? formData.incomeTaxRegime : null,
            tdsType: formData.includeInPayroll === 'Yes' ? formData.tdsRateType : null,
            taxPercentage: formData.includeInPayroll === 'Yes' && formData.tdsRateType === 'Tax Percentage for Consultant' ? Number(formData.tdsPercentage) : null,
            professionTaxExemption: formData.includeInPayroll === 'Yes' ? formData.isAdditionalTaxExempt : null,
            exemptionCategory: formData.includeInPayroll === 'Yes' && formData.isAdditionalTaxExempt ? exemptionCategory : null
        };

        return payload;
    };

    const handleSaveChanges = async () => {
        const errors = validateForm(formData);
        setValidationErrors(errors);
        if (Object.keys(errors).length > 0) {
            const errorFields = Object.keys(errors);
            let errorMessage = 'Please fix the following errors:\n\n';

            errorFields.forEach((field, index) => {
                const fieldLabel = getFieldLabel(field);
                errorMessage += `${index + 1}. ${fieldLabel}: ${errors[field]}\n`;
            });

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
            return;
        }

        if (initialPayrollData && !hasFormDataChanged()) {
            setSnackbar({
                open: true,
                message: 'No changes detected to update.',
                severity: 'info'
            });

            if (navigationContext?.returnToRehire && onSaveAndReturn) {
                onSaveAndReturn();
            }
            return;
        }

        const payload = preparePayload();

        try {
            let response;
            let isUpdate = false;

            if (initialPayrollData) {
                response = await putData(API_ENDPOINTS.updatePayrollSettings, payload);
                isUpdate = true;
            } else {
                response = await postData(API_ENDPOINTS.createPayrollSettings, payload);
            }

            if (response.status === 'SUCCESS') {
                setSnackbar({
                    open: true,
                    message: response.message || `Payroll Details ${isUpdate ? 'Updated' : 'Saved'} Successfully!`,
                    severity: 'success'
                });
                setInitialPayrollData(response.result || payload);
                if (navigationContext?.returnToRehire && onSaveAndReturn) {
                    onSaveAndReturn();
                }
            }
        } catch (error) {
            const apiError = error.response?.data?.message || error.response?.data?.result || 'An unexpected error occurred';
            setSnackbar({ open: true, message: apiError, severity: 'error' });
        }
    };

    const handleCompensationDetailsClick = () => {
        if (typeof onNavigateToPayrollCompensation === "function") {
            onNavigateToPayrollCompensation(empIdFromUrl);
            return;
        }
    };

    const createToggleSection = (name, label, reasonLabel, conditionalChildren) => {
        const exemptionFieldMap = {
            'includePf': 'pfExemptionReason',
            'includeEsi': 'esiExemptionReason',
            'includeGratuity': 'gratuityExemptionReason'
        };

        const exemptionReasonField = exemptionFieldMap[name];

        return (
            <Grid size={{ xs: 12 }}>
                <FormControl component="fieldset" fullWidth sx={{ border: '1px solid #cccccc', p: 1.5, borderRadius: 1 }}>
                    <Typography variant="subtitle1" component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                        {label}
                    </Typography>
                    <RadioGroup row name={name} value={formData[name]} onChange={handleRadioChange} sx={{ mb: 1 }}>
                        <FormControlLabel value="Yes" control={<Radio size="small" color="primary" />} label="Yes (Include)" />
                        <FormControlLabel value="No" control={<Radio size="small" color="primary" />} label="No (Exempt)" />
                    </RadioGroup>

                    {formData[name] === 'Yes' ? (
                        <Grid container spacing={2} sx={{ mt: 0 }}>
                            {conditionalChildren}
                        </Grid>
                    ) : (
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label={reasonLabel}
                            name={exemptionReasonField}
                            value={formData[exemptionReasonField]}
                            onChange={handleInputChange}
                            required
                            size="small"
                            sx={{ mt: 1 }}
                            error={!!validationErrors[exemptionReasonField]}
                            helperText={validationErrors[exemptionReasonField] || "This field is required when exemption is selected"}
                        />
                    )}
                </FormControl>
            </Grid>
        );
    };

    return (
        <Box sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', my: 2 }}>
                <Box sx={{ flexGrow: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                        Payroll Information
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    {/* {formData.includeInPayroll === 'Yes' && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleCompensationDetailsClick}
                            size="small"
                            sx={{ textTransform: 'none', marginRight: 20 }}
                        >
                            Add Compensation
                        </Button>
                    )} */}
                    <Box sx={{ width: { xs: 110, sm: 220 }, textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                            {`Form Completion: ${completion}%`}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={completion}
                            sx={{ height: 8, borderRadius: 5, mt: 0.5, backgroundColor: '#e0e0e0' }}
                            color={completion === 100 ? 'success' : 'primary'}
                        />
                    </Box>
                </Stack>
            </Stack>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <FormControl component="fieldset" fullWidth sx={{ border: '1px solid #1976d2', p: 1.5, borderRadius: 1 }}>
                        <Typography variant="subtitle1" component="legend" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>
                            Include Employee in Payroll Cycle (Mandatory)
                        </Typography>
                        <RadioGroup row name="includeInPayroll" value={formData.includeInPayroll} onChange={handleRadioChange}>
                            <FormControlLabel value="Yes" control={<Radio size="small" color="primary" />} label="Yes (Process Payroll)" />
                            <FormControlLabel value="No" control={<Radio size="small" color="primary" />} label="No (Exclude)" />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            </Grid>

            {formData.includeInPayroll === 'Yes' ? (
                <>
                    <Grid container spacing={2} mt={4}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth required size="small" error={!!validationErrors.payrollBatch}>
                                <InputLabel>Payroll Batch</InputLabel>
                                <Select
                                    label="Payroll Batch"
                                    name="payrollBatch"
                                    value={formData.payrollBatch}
                                    onChange={handleInputChange}
                                >
                                    {payrollBatchOptions.map(option => (
                                        <MenuItem key={option} value={option}>
                                            {formatDisplayText(option)}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {validationErrors.payrollBatch && (
                                    <FormHelperText error>{validationErrors.payrollBatch}</FormHelperText>
                                )}
                                <FormHelperText>Full Time, Part Time</FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={2}>
                        <Grid size={{ xs: 12 }}>
                            {createToggleSection(
                                'includePf',
                                'Include Provident Fund (PF)',
                                'Reason for PF Exemption',
                                [
                                    <Grid size={{ xs: 12, sm: 6, md: 6 }} key="pfSlab">
                                        <FormControl fullWidth required size="small" error={!!validationErrors.pfSlabType}>
                                            <InputLabel>PF Contribution Rate/Slab</InputLabel>
                                            <Select label="PF Contribution Rate/Slab" name="pfSlabType" value={formData.pfSlabType} onChange={handleInputChange}>
                                                {pfSlabOptions.map(option => (
                                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                                ))}
                                            </Select>
                                            {validationErrors.pfSlabType && (
                                                <FormHelperText error>{validationErrors.pfSlabType}</FormHelperText>
                                            )}
                                            <FormHelperText>Restrict to 15,000 Slab or Actual Wage options</FormHelperText>
                                        </FormControl>
                                    </Grid>,
                                    <Grid size={{ xs: 12, sm: 3, md: 3 }} key="employeePfRate">
                                        <FormControl fullWidth required size="small" error={!!validationErrors.employeePfRate}>
                                            <InputLabel>Employee PF Rate</InputLabel>
                                            <Select
                                                label="Employee PF Rate"
                                                name="employeePfRate"
                                                value={formData.employeePfRate}
                                                onChange={handleInputChange}
                                            >
                                                {pfRateOptions.map(option => (
                                                    <MenuItem key={option} value={String(option)}>{option}%</MenuItem>
                                                ))}
                                            </Select>
                                            {validationErrors.employeePfRate && (
                                                <FormHelperText error>{validationErrors.employeePfRate}</FormHelperText>
                                            )}
                                            <FormHelperText>Employee PF Rate (in %)</FormHelperText>
                                        </FormControl>
                                    </Grid>,
                                    <Grid size={{ xs: 12, sm: 3, md: 3 }} key="employerPfRate">
                                        <FormControl fullWidth required size="small" error={!!validationErrors.employerPfRate}>
                                            <InputLabel>Employer PF Rate</InputLabel>
                                            <Select label="Employer PF Rate" name="employerPfRate" value={formData.employerPfRate} onChange={handleInputChange}>
                                                {employerPfRateOptions.map(option => (
                                                    <MenuItem key={option} value={String(option)}>{option}%</MenuItem>
                                                ))}
                                            </Select>
                                            {validationErrors.employerPfRate && (
                                                <FormHelperText error>{validationErrors.employerPfRate}</FormHelperText>
                                            )}
                                            <FormHelperText>Employer PF Rate (in %)</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                ]
                            )}

                            <Box sx={{ my: 3 }}>
                                {createToggleSection(
                                    'includeEsi',
                                    'Include Employees\' State Insurance (ESI)',
                                    'Reason for ESI Exemption',
                                    [
                                        <Grid size={{ xs: 12, sm: 6 }} key="esiSlab">
                                            <FormControl fullWidth required size="small" error={!!validationErrors.esicSlabType}>
                                                <InputLabel>ESIC Contribution Basis</InputLabel>
                                                <Select
                                                    label="ESIC Contribution Basis"
                                                    name="esicSlabType"
                                                    value={formData.esicSlabType}
                                                    onChange={handleInputChange}
                                                >
                                                    {esicSlabOptions.map(option => (
                                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                                    ))}
                                                </Select>
                                                {validationErrors.esicSlabType && (
                                                    <FormHelperText error>{validationErrors.esicSlabType}</FormHelperText>
                                                )}
                                                <FormHelperText>Restrict contribution to Employee Gross ₹21,000 Slab</FormHelperText>
                                            </FormControl>
                                        </Grid>,
                                    ]
                                )}
                            </Box>

                            <Typography variant='h6' fontWeight={600} mt={2} mb={1} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>Other Benefits</Typography>
                            {createToggleSection(
                                'includeGratuity',
                                'Include Gratuity',
                                'Reason for Gratuity Exemption',
                                []
                            )}
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} my={4}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth required size="small" error={!!validationErrors.hraType}>
                                <InputLabel>HRA Calculation Basis</InputLabel>
                                <Select label="HRA Calculation Basis" name="hraType" value={formData.hraType} onChange={handleInputChange}>
                                    {hraOptions.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                                {validationErrors.hraType && (
                                    <FormHelperText error>{validationErrors.hraType}</FormHelperText>
                                )}
                                <FormHelperText>50% for Metropolitan / 40% for Non-Metropolitan</FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Typography variant='h6' fontWeight={600} my={2} mb={1} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>Income Tax and TDS Settings</Typography>
                    <Grid container spacing={2} mt={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth required size="small" error={!!validationErrors.incomeTaxRegime}>
                                <InputLabel>Income Tax Regime</InputLabel>
                                <Select label="Income Tax Regime" name="incomeTaxRegime" value={formData.incomeTaxRegime} onChange={handleInputChange}>
                                    {incomeTaxRegimeOptions.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                                {validationErrors.incomeTaxRegime && (
                                    <FormHelperText error>{validationErrors.incomeTaxRegime}</FormHelperText>
                                )}
                                <FormHelperText>Old Regime or New Regime</FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth required size="small" error={!!validationErrors.tdsRateType}>
                                <InputLabel>TDS Rate Type</InputLabel>
                                <Select label="TDS Rate Type" name="tdsRateType" value={formData.tdsRateType} onChange={handleInputChange}>
                                    {tdsRateOptions.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                                {validationErrors.tdsRateType && (
                                    <FormHelperText error>{validationErrors.tdsRateType}</FormHelperText>
                                )}
                                <FormHelperText>Tax Slab (Salaried) or Percentage (Consultant)</FormHelperText>
                            </FormControl>
                        </Grid>
                        {formData.tdsRateType === 'Tax Percentage for Consultant' && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="TDS/Tax Percentage (for Consultants)"
                                    name="tdsPercentage"
                                    type="number"
                                    value={formData.tdsPercentage}
                                    onChange={handleInputChange}
                                    required
                                    error={!!validationErrors.tdsPercentage}
                                    helperText={validationErrors.tdsPercentage || "Required field for consultant TDS rate"}
                                    size="small"
                                />
                            </Grid>
                        )}
                    </Grid>

                    <Typography variant='h6' fontWeight={600} my={2} mb={1} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>Tax Exemptions (Optional)</Typography>
                    <Grid container spacing={2} mt={2}>
                        <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isAdditionalTaxExempt}
                                        onChange={handleCheckboxChange}
                                        name="isAdditionalTaxExempt"
                                        color="primary"
                                        size="small"
                                    />
                                }
                                label="Profession Tax Exemption (Yes/No)"
                                sx={{ fontWeight: 600 }}
                            />
                        </Grid>
                    </Grid>
                    {formData.isAdditionalTaxExempt && (
                        <Grid container size={{ xs: 12 }} spacing={1} sx={{ pl: 3, pt: 1 }}>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox checked={formData.isSeniorCitizen} onChange={handleCheckboxChange} name="isSeniorCitizen" color="primary" size="small" />
                                    }
                                    label="Senior Citizen"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox checked={formData.isOtherlyDisabled} onChange={handleCheckboxChange} name="isOtherlyDisabled" color="primary" size="small" />
                                    }
                                    label="Otherly Disabled"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox checked={formData.isPhysicallyHandicapped} onChange={handleCheckboxChange} name="isPhysicallyHandicapped" color="primary" size="small" />
                                    }
                                    label="Physically Handicapped"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox checked={formData.othersExemption} onChange={handleCheckboxChange} name="othersExemption" color="primary" size="small" />
                                    }
                                    label="Others"
                                />
                            </Grid>
                        </Grid>
                    )}
                </>
            ) : (
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Reason for Payroll Exclusion"
                            name="payrollExclusionReason"
                            value={formData.payrollExclusionReason}
                            onChange={handleInputChange}
                            required
                            sx={{ mt: 2 }}
                            error={!!validationErrors.payrollExclusionReason}
                            helperText={validationErrors.payrollExclusionReason || "This field is required when payroll is excluded"}
                        />
                    </Grid>
                </Grid>
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                {navigationContext?.returnToRehire ? (
                    <Paper
                        elevation={3}
                        sx={{
                            width: '100%',
                            p: 2,
                            bgcolor: 'info.light',
                            borderLeft: '4px solid',
                            borderColor: 'info.main'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Rehire Verification in Progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Completing verification for: <strong>{navigationContext.stepLabel || 'Personal & Contact Details'}</strong>
                                </Typography>
                            </Box>
                            <Button variant="contained" color="info" onClick={handleSaveChanges} disabled={completion !== 100} sx={{ textTransform: 'none' }} >
                                Verify & Return to Rehire
                            </Button>
                        </Box>
                    </Paper>
                ) : (
                    <Button variant="contained" color="primary" onClick={handleSaveChanges} sx={{ py: 1.5, px: 4, textTransform: 'none' }} >
                        Save Payroll Settings
                    </Button>
                )}
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default CompensationPay;